import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MLAnalyticsApi } from '../../services/api/MLAnalyticsApi';
import {
  MetricCard, LoadingChart, EmptyState, InsightCard, ProgressBar
} from '../../components/analytics/SharedAnalyticsComponents';
import PdfExportButton from '../../components/analytics/PdfExportButton';
import { ReportGenerator } from '../../utils/ReportGenerator';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'churn', label: 'Churn Risk' },
  { key: 'segments', label: 'User Segments' },
  { key: 'forecast', label: 'Cost Forecast' },
  { key: 'anomalies', label: 'Anomalies' },
  { key: 'models', label: 'Model Status' },
];

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6'];
const RISK_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };
const SEVERITY_COLORS = { critical: '#ef4444', warning: '#f59e0b', info: '#3b82f6' };

/** Format large numbers to human-readable (e.g. 505547167 → "505.5M") */
function fmtNum(v) {
  if (v == null || isNaN(v)) return '-';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e4) return `${(v / 1e3).toFixed(1)}K`;
  if (Number.isInteger(v)) return v.toLocaleString();
  return v.toFixed(1);
}

/** Convert seconds to human-readable duration */
function fmtDuration(secs) {
  if (secs == null || isNaN(secs)) return '-';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
}

/** Normalize subscription status for display */
function fmtSubscription(status) {
  if (!status || status === 'nan' || status === 'null' || status === 'None' || status === 'undefined') return 'Free';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export default function MLInsightsDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [insights, setInsights] = useState(null);
  const [churn, setChurn] = useState(null);
  const [segments, setSegments] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [costAnomalies, setCostAnomalies] = useState(null);
  const [sessionAnomalies, setSessionAnomalies] = useState(null);
  const [modelStatus, setModelStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [training, setTraining] = useState(false);
  const [trainResult, setTrainResult] = useState(null);
  const [forecastDays, setForecastDays] = useState(30);
  const [churnTop, setChurnTop] = useState(50);
  const [anomalyDays, setAnomalyDays] = useState(7);
  const dashboardRef = useRef(null);

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  // ── Data Loading ──────────────────────────────────────────────

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getInsights();
      setInsights(data);
    } catch (e) {
      console.error('Failed to load ML insights:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChurn = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getChurnPredictions(churnTop);
      setChurn(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [churnTop]);

  const loadSegments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getSegments();
      setSegments(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadForecast = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getForecast(forecastDays);
      setForecast(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [forecastDays]);

  const loadAnomalies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [cost, session] = await Promise.all([
        MLAnalyticsApi.getCostAnomalies(anomalyDays),
        MLAnalyticsApi.getSessionAnomalies(anomalyDays),
      ]);
      setCostAnomalies(cost);
      setSessionAnomalies(session);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [anomalyDays]);

  const loadModelStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getStatus();
      setModelStatus(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const loaders = {
      overview: loadOverview,
      churn: loadChurn,
      segments: loadSegments,
      forecast: loadForecast,
      anomalies: loadAnomalies,
      models: loadModelStatus,
    };
    loaders[activeTab]?.();
  }, [activeTab, loadOverview, loadChurn, loadSegments, loadForecast, loadAnomalies, loadModelStatus]);

  // ── Training ──────────────────────────────────────────────────

  const handleTrain = async (modelName) => {
    // Confirm before triggering potentially expensive training
    const label = modelName ? modelName.replace(/_/g, ' ') : 'all models';
    if (!window.confirm(`Are you sure you want to train ${label}? This may take several minutes.`)) return;

    try {
      setTraining(true);
      setTrainResult(null);
      const res = modelName
        ? await MLAnalyticsApi.trainModel(modelName)
        : await MLAnalyticsApi.trainAll();
      setTrainResult({ success: true, data: res });
      // Refresh status
      const status = await MLAnalyticsApi.getStatus();
      setModelStatus(status);
    } catch (e) {
      setTrainResult({ success: false, error: e.message });
    } finally {
      setTraining(false);
    }
  };

  // ── PDF Export ────────────────────────────────────────────────

  const handleExport = async () => {
    const gen = new ReportGenerator('ML Analytics Insights');
    gen.addMetricsCards([
      { label: 'Active Models', value: (() => {
        if (modelStatus?.models && Object.keys(modelStatus.models).length > 0) return Object.keys(modelStatus.models).length;
        const mk = ['churn', 'segments', 'forecast', 'anomalies', 'cost_forecast', 'anomaly_detection', 'user_segments', 'churn_prediction'];
        const count = mk.filter(k => modelStatus?.[k]?.trained_at || modelStatus?.[k]?.trained).length;
        return count || '-';
      })() },
      { label: 'High-Risk Users', value: (insights?.churn?.predictions || insights?.churn?.top_at_risk || insights?.churn?.users || []).filter(u => u.risk_level === 'high').length || '-' },
      { label: 'User Segments', value: (insights?.segments?.cluster_summaries || insights?.segments?.clusters || []).length || '-' },
      { label: 'Forecast Trend', value: insights?.forecast?.trend ?? '-' },
    ]);
    await gen.captureChart(dashboardRef.current, 'ML Dashboard Snapshot');
    return gen.save('ml-insights-report.pdf');
  };

  // ── Tab Rendering ─────────────────────────────────────────────

  const renderOverview = () => {
    if (!insights) return <LoadingChart height={400} />;
    const { churn: ch, segments: seg, forecast: fc, anomalies: anom } = insights;

    const churnPreds = ch?.predictions || ch?.top_at_risk || ch?.users || [];
    const churnByRisk = churnPreds.reduce((acc, u) => {
      acc[u.risk_level] = (acc[u.risk_level] || 0) + 1;
      return acc;
    }, {});

    const clusterList = seg?.cluster_summaries || seg?.clusters || [];
    const clusterData = clusterList.map((c, i) => ({
      name: c.label || `Cluster ${c.cluster_id}`,
      value: c.size,
      fill: COLORS[i % COLORS.length],
    }));

    const forecastData = (fc?.daily_forecast || []).map(d => ({
      date: d.date?.slice(5) || d.ds?.slice(5),
      cost: Number((d.predicted_cost ?? d.yhat ?? 0).toFixed(2)),
      lower: Number((d.lower_bound ?? d.yhat_lower ?? 0).toFixed(2)),
      upper: Number((d.upper_bound ?? d.yhat_upper ?? 0).toFixed(2)),
    }));

    const costAnomList = Array.isArray(anom?.cost_anomalies) ? anom.cost_anomalies : (anom?.recent_cost || []);
    const sessAnomList = Array.isArray(anom?.session_anomalies) ? anom.session_anomalies : (anom?.recent_sessions || []);
    const costAnomCount = Array.isArray(anom?.cost_anomalies) ? anom.cost_anomalies.length : (typeof anom?.cost_anomalies === 'number' ? anom.cost_anomalies : costAnomList.length);

    return (
      <div>
        {/* KPI row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <MetricCard label="High-Risk Users" value={churnByRisk.high || 0} color="#ef4444" />
          <MetricCard label="Medium-Risk Users" value={churnByRisk.medium || 0} color="#f59e0b" />
          <MetricCard label="User Segments" value={clusterData.length} color="#6366f1" />
          <MetricCard label="Forecast Trend" value={fc?.trend || 'N/A'} color={fc?.trend === 'increasing' ? '#ef4444' : '#22c55e'} />
          <MetricCard label="Cost Anomalies" value={costAnomCount} color="#f59e0b" />
        </div>

        {/* Two-col layout: Segments pie + Forecast chart */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
          {/* Segments */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>User Segments</h3>
            {clusterData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={clusterData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {clusterData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState message="No segment data" icon="🔬" />}
          </div>

          {/* Forecast mini */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>7-Day Cost Forecast</h3>
            {forecastData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={forecastData.slice(0, 7)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
                  <Tooltip formatter={v => `$${v}`} />
                  <Line type="monotone" dataKey="upper" stroke="#e5e7eb" strokeDasharray="3 3" dot={false} name="Upper Bound" />
                  <Line type="monotone" dataKey="cost" stroke="#6366f1" strokeWidth={2} dot={false} name="Predicted" />
                  <Line type="monotone" dataKey="lower" stroke="#e5e7eb" strokeDasharray="3 3" dot={false} name="Lower Bound" />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyState message="No forecast data — train the model first" icon="📈" />}
          </div>
        </div>

        {/* Churn risk table (top 10) */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Top Churn Risks</h3>
          {churnPreds.length > 0 ? (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Risk Level</th>
                  <th style={thStyle}>Probability</th>
                  <th style={thStyle}>Sessions</th>
                  <th style={thStyle}>Days Inactive</th>
                </tr>
              </thead>
              <tbody>
                {churnPreds.slice(0, 10).map((u, i) => (
                    <tr key={i} style={i % 2 === 0 ? { background: 'var(--table-row-alt-bg, var(--panel-bg, #f9fafb))' } : {}}>
                    <td style={tdStyle}>{u.email || u.user_id}</td>
                    <td style={tdStyle}><RiskBadge level={u.risk_level} /></td>
                    <td style={tdStyle}>
                      <ProgressBar value={Math.round(u.churn_probability * 100)} max={100} color={RISK_COLORS[u.risk_level]} height={8} />
                      <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>{(u.churn_probability * 100).toFixed(1)}%</span>
                    </td>
                    <td style={tdStyle}>{u.total_sessions ?? '-'}</td>
                    <td style={tdStyle}>{u.days_since_last_activity ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState message="No churn predictions — train the churn model first" icon="🔮" />}
        </div>

        {/* Recent anomalies */}
        {(costAnomList.length > 0 || sessAnomList.length > 0) && (
          <div style={{ ...cardStyle, marginTop: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Recent Anomalies</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {costAnomList.slice(0, 5).map((a, i) => (
                <InsightCard key={`cost-${i}`} title={`Cost Anomaly — ${a.date || 'Unknown'}`} description={`User ${a.user_id}: $${a.cost?.toFixed(2)} (${a.tokens} tokens)`} severity={a.severity === 'critical' ? 'danger' : 'warning'} icon="💰" />
              ))}
              {sessAnomList.slice(0, 5).map((a, i) => (
                <InsightCard key={`sess-${i}`} title={`Session Anomaly — ${a.session_id?.slice(0, 8) || 'Unknown'}`} description={`Duration: ${a.duration_seconds}s, Events: ${a.event_count}`} severity={a.severity === 'critical' ? 'danger' : 'warning'} icon="⚡" />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderChurn = () => {
    if (!churn) return <LoadingChart height={400} />;
    const predictions = churn.predictions || churn.users || [];

    const riskDist = predictions.reduce((acc, u) => {
      acc[u.risk_level] = (acc[u.risk_level] || 0) + 1;
      return acc;
    }, {});
    const riskChartData = Object.entries(riskDist).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, fill: RISK_COLORS[k] }));

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Churn Risk Predictions</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#6b7280' }}>Show top:</label>
            <select value={churnTop} onChange={e => setChurnTop(Number(e.target.value))} style={selectStyle}>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Risk distribution bar */}
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 24, marginBottom: 24 }}>
          <div style={cardStyle}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Risk Distribution</h4>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={riskChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {riskChartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={cardStyle}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Model Metrics</h4>
            {(churn.model_metrics || churn.model_info?.metrics) ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <MetricCard label="Accuracy" value={`${((churn.model_metrics || churn.model_info?.metrics)?.accuracy <= 1 ? ((churn.model_metrics || churn.model_info?.metrics).accuracy * 100) : (churn.model_metrics || churn.model_info?.metrics).accuracy).toFixed(1)}%`} color="#22c55e" />
                <MetricCard label="AUC-ROC" value={((churn.model_metrics || churn.model_info?.metrics)?.auc_roc || 0).toFixed(3)} color="#6366f1" />
                <MetricCard label="F1 Score" value={((churn.model_metrics || churn.model_info?.metrics)?.f1 || 0).toFixed(3)} color="#f59e0b" />
              </div>
            ) : <EmptyState message="No metrics" icon="📊" />}
          </div>
        </div>

        {/* Users table */}
        <div style={cardStyle}>
          {predictions.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Risk</th>
                    <th style={thStyle}>Probability</th>
                    <th style={thStyle}>Sessions</th>
                    <th style={thStyle}>Study Time (min)</th>
                    <th style={thStyle}>Engagement</th>
                    <th style={thStyle}>Days Inactive</th>
                  </tr>
                </thead>
                <tbody>
                  {predictions.map((u, i) => (
                    <tr key={i} style={i % 2 === 0 ? { background: 'var(--table-row-alt-bg, var(--panel-bg, #f9fafb))' } : {}}>
                      <td style={tdStyle}>{i + 1}</td>
                      <td style={tdStyle}>{u.email || u.user_id}</td>
                      <td style={tdStyle}><RiskBadge level={u.risk_level} /></td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ProgressBar value={Math.round(u.churn_probability * 100)} max={100} color={RISK_COLORS[u.risk_level]} height={6} />
                          <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{(u.churn_probability * 100).toFixed(1)}%</span>
                        </div>
                      </td>
                      <td style={tdStyle}>{u.total_sessions ?? '-'}</td>
                      <td style={tdStyle}>{u.total_study_time_minutes != null ? fmtDuration(u.total_study_time_minutes * 60) : '-'}</td>
                      <td style={tdStyle}>{u.engagement_score != null ? fmtNum(u.engagement_score) : '-'}</td>
                      <td style={tdStyle}>{u.days_since_last_activity ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState message="No churn data available — train the model" icon="🔮" />}
        </div>
      </div>
    );
  };

  const renderSegments = () => {
    if (!segments) return <LoadingChart height={400} />;
    const clusters = segments.cluster_summaries || segments.clusters || [];
    const users = segments.users || [];

    const distData = clusters.map((c, i) => ({
      name: c.label || `Cluster ${c.cluster_id}`,
      size: c.size,
      fill: COLORS[i % COLORS.length],
    }));

    return (
      <div>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>User Segments</h3>

        {/* Distribution chart */}
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Segment Distribution</h4>
          {distData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={distData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 12 }} label={{ value: 'Users', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Bar dataKey="size" name="Users">
                  {distData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState message="No segments" icon="🔬" />}
        </div>

        {/* Cluster cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
          {clusters.map((c, i) => (
            <div key={c.cluster_id} style={{ ...cardStyle, borderLeft: `4px solid ${COLORS[i % COLORS.length]}` }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600 }}>{c.label || `Cluster ${c.cluster_id}`}</h4>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>{c.size} users</p>
              {c.centroid && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12, color: '#374151' }}>
                  {Object.entries(c.centroid).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                      <span style={{ color: '#6b7280' }}>{k.replace(/_/g, ' ')}:</span>
                      <span style={{ fontWeight: 500 }}>{k === 'subscription_status' ? fmtSubscription(v) : typeof v === 'number' ? fmtNum(v) : v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Users list (first 30) */}
        {users.length > 0 && (
          <div style={cardStyle}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>User Assignments ({users.length} total)</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Segment</th>
                    <th style={thStyle}>Distance</th>
                  </tr>
                </thead>
                <tbody>
                  {users.slice(0, 30).map((u, i) => (
                    <tr key={i} style={i % 2 === 0 ? { background: 'var(--table-row-alt-bg, var(--panel-bg, #f9fafb))' } : {}}>
                      <td style={tdStyle}>{u.email || u.user_id}</td>
                      <td style={tdStyle}>
                        <span style={{ ...badgeBase, background: `${COLORS[u.cluster_id % COLORS.length]}20`, color: COLORS[u.cluster_id % COLORS.length] }}>
                          {u.cluster_label || `Cluster ${u.cluster_id}`}
                        </span>
                      </td>
                      <td style={tdStyle}>{u.distance_to_centroid?.toFixed(2) ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length > 30 && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9ca3af' }}>Showing 30 of {users.length} users</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderForecast = () => {
    if (!forecast) return <LoadingChart height={400} />;
    const daily = (forecast.daily_forecast || []).map(d => ({
      date: d.date || d.ds,
      cost: Number((d.predicted_cost ?? d.yhat ?? 0).toFixed(2)),
      lower: Number((d.lower_bound ?? d.yhat_lower ?? 0).toFixed(2)),
      upper: Number((d.upper_bound ?? d.yhat_upper ?? 0).toFixed(2)),
    }));

    const projections = forecast.projections || {};
    const metrics = forecast.model_metrics || forecast.model_info?.metrics || {};

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>GPT Cost Forecast</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#6b7280' }}>Forecast horizon:</label>
            <select value={forecastDays} onChange={e => { setForecastDays(Number(e.target.value)); }} style={selectStyle}>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </div>

        {/* KPI cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
          <MetricCard label="Trend" value={forecast.trend || 'N/A'} color={forecast.trend === 'increasing' ? '#ef4444' : '#22c55e'} />
          <MetricCard label="Weekly Projection" value={projections.weekly_total ? `$${projections.weekly_total.toFixed(2)}` : '-'} color="#6366f1" />
          <MetricCard label="Monthly Projection" value={projections.monthly_total ? `$${projections.monthly_total.toFixed(2)}` : '-'} color="#8b5cf6" />
          <MetricCard label="Avg Daily Cost" value={metrics.avg_daily_cost ? `$${metrics.avg_daily_cost.toFixed(2)}` : '-'} color="#06b6d4" />
          {metrics.mae && <MetricCard label="MAE" value={`$${metrics.mae.toFixed(3)}`} color="#f59e0b" />}
        </div>

        {/* Forecast line chart */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Daily Forecast with Confidence Interval</h4>
          {daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={v => `$${v}`} />
                <Legend />
                <Line type="monotone" dataKey="upper" stroke="#c7d2fe" strokeDasharray="5 5" dot={false} name="Upper Bound (80%)" />
                <Line type="monotone" dataKey="cost" stroke="#6366f1" strokeWidth={2.5} dot={{ fill: '#6366f1', r: 2 }} name="Predicted Cost" />
                <Line type="monotone" dataKey="lower" stroke="#c7d2fe" strokeDasharray="5 5" dot={false} name="Lower Bound (80%)" />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyState message="No forecast data — train the model first" icon="📈" />}
        </div>

        {/* Weekly / Monthly breakdown */}
        {(projections.weekly_daily_avg != null || projections.monthly_daily_avg != null) && (
          <div style={{ ...cardStyle, marginTop: 24 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Projections</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 14 }}>
              <div>
                <p style={projLabel}>Weekly</p>
                <p style={projValue}>Total: ${projections.weekly_total?.toFixed(2) ?? '-'}</p>
                <p style={projValue}>Daily Avg: ${projections.weekly_daily_avg?.toFixed(2) ?? '-'}</p>
                <p style={projValue}>Range: ${projections.weekly_range?.min?.toFixed(2) ?? '?'} – ${projections.weekly_range?.max?.toFixed(2) ?? '?'}</p>
              </div>
              <div>
                <p style={projLabel}>Monthly</p>
                <p style={projValue}>Total: ${projections.monthly_total?.toFixed(2) ?? '-'}</p>
                <p style={projValue}>Daily Avg: ${projections.monthly_daily_avg?.toFixed(2) ?? '-'}</p>
                <p style={projValue}>Range: ${projections.monthly_range?.min?.toFixed(2) ?? '?'} – ${projections.monthly_range?.max?.toFixed(2) ?? '?'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAnomalies = () => {
    const costList = costAnomalies?.anomalies || [];
    const sessList = sessionAnomalies?.anomalies || [];
    const noData = costList.length === 0 && sessList.length === 0;

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Anomaly Detection</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#6b7280' }}>Window:</label>
            <select value={anomalyDays} onChange={e => setAnomalyDays(Number(e.target.value))} style={selectStyle}>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>
        </div>

        {loading ? <LoadingChart height={300} /> : noData ? (
          <EmptyState message="No anomalies detected in this window — try a longer window or train the model first" icon="✅" />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Cost anomalies */}
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>💰 Cost Anomalies ({costList.length})</h4>
              {costList.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {costList.map((a, i) => (
                    <div key={i} style={{ ...anomalyRow, borderLeft: `3px solid ${SEVERITY_COLORS[a.severity] || '#9ca3af'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>User {a.user_id}</span>
                        <SeverityBadge severity={a.severity} />
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Date: {a.date} · Cost: ${a.cost?.toFixed(2)} · Tokens: {a.tokens?.toLocaleString()} · Score: {a.anomaly_score?.toFixed(3)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <EmptyState message="No cost anomalies" icon="✅" />}
            </div>

            {/* Session anomalies */}
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>⚡ Session Anomalies ({sessList.length})</h4>
              {sessList.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sessList.map((a, i) => (
                    <div key={i} style={{ ...anomalyRow, borderLeft: `3px solid ${SEVERITY_COLORS[a.severity] || '#9ca3af'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Session {(a.session_id || '').slice(0, 12)}</span>
                        <SeverityBadge severity={a.severity} />
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Duration: {a.duration_seconds}s · Events: {a.event_count} · Score: {a.anomaly_score?.toFixed(3)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <EmptyState message="No session anomalies" icon="✅" />}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderModels = () => {
    if (!modelStatus) return <LoadingChart height={300} />;

    // API returns model info at top level keys (churn, segments, forecast, anomalies)
    // rather than under a nested "models" object
    const models = modelStatus.models || {};
    const MODEL_KEYS = ['churn', 'segments', 'forecast', 'anomalies', 'cost_forecast', 'anomaly_detection', 'user_segments', 'churn_prediction'];
    if (Object.keys(models).length === 0) {
      // Try extracting from top-level keys
      for (const key of MODEL_KEYS) {
        if (modelStatus[key] && typeof modelStatus[key] === 'object' && (modelStatus[key].trained_at !== undefined || modelStatus[key].trained !== undefined)) {
          models[key] = modelStatus[key];
        }
      }
    }

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Model Status & Training</h3>
          {isAdmin && (
            <button onClick={() => handleTrain(null)} disabled={training} style={{ ...btnPrimary, opacity: training ? 0.6 : 1 }}>
              {training ? 'Training...' : 'Train All Models'}
            </button>
          )}
        </div>

        {trainResult && (
          <div style={{ ...cardStyle, marginBottom: 16, background: trainResult.success ? '#f0fdf4' : '#fef2f2', borderLeft: `4px solid ${trainResult.success ? '#22c55e' : '#ef4444'}` }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>
              {trainResult.success ? '✅ Training completed successfully' : `❌ Training failed: ${trainResult.error}`}
            </p>
            {trainResult.success && trainResult.data?.results && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#374151' }}>
                {Object.entries(trainResult.data.results).map(([name, r]) => (
                  <div key={name}>{name}: {r.status || 'ok'} {r.error ? `— ${r.error}` : ''}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {Object.entries(models).map(([name, info]) => (
            <div key={name} style={{ ...cardStyle, borderTop: `3px solid ${info.trained ? '#22c55e' : '#d1d5db'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, textTransform: 'capitalize' }}>{name.replace(/_/g, ' ')}</h4>
                <span style={{ ...badgeBase, background: info.trained ? '#dcfce7' : '#f3f4f6', color: info.trained ? '#166534' : '#6b7280' }}>
                  {info.trained ? 'Trained' : 'Not Trained'}
                </span>
              </div>

              {info.trained_at && (
                <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af' }}>
                  Trained: {new Date(info.trained_at).toLocaleString()}
                </p>
              )}

              {info.metrics && (
                <div style={{ fontSize: 12, color: '#374151' }}>
                  {Object.entries(info.metrics).slice(0, 6).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ color: '#6b7280' }}>{k.replace(/_/g, ' ')}:</span>
                      <span style={{ fontWeight: 500 }}>{typeof v === 'number' ? v.toFixed(4) : JSON.stringify(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              {isAdmin && (
                <button onClick={() => handleTrain(name)} disabled={training} style={{ ...btnSecondary, marginTop: 12, width: '100%', opacity: training ? 0.6 : 1 }}>
                  {training ? 'Training...' : `Retrain ${name}`}
                </button>
              )}
            </div>
          ))}

          {Object.keys(models).length === 0 && (
            <EmptyState message="No models registered yet — click 'Train All Models' above to initialize churn, segmentation, forecast, and anomaly models" icon="🤖" />
          )}
        </div>
      </div>
    );
  };

  const tabContent = {
    overview: renderOverview,
    churn: renderChurn,
    segments: renderSegments,
    forecast: renderForecast,
    anomalies: renderAnomalies,
    models: renderModels,
  };

  // ── Main Render ───────────────────────────────────────────────

  return (
    <div ref={dashboardRef} style={{ padding: 24, maxWidth: 1400, margin: '0 auto', height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>ML Insights</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6b7280' }}>
            Machine-learning powered analytics — churn prediction, user segmentation, cost forecasting & anomaly detection
          </p>
        </div>
        <PdfExportButton onExport={handleExport} label="Export Report" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 24 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? '#6366f1' : '#6b7280', background: 'none', border: 'none',
              borderBottom: activeTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -2, transition: 'all 0.2s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Error bar */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#991b1b', fontSize: 14 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Tab content */}
      {tabContent[activeTab]?.()}
    </div>
  );
}

// ── Helper Components ─────────────────────────────────────────

function RiskBadge({ level }) {
  const colors = {
    high:   { bg: 'rgba(239, 68, 68, 0.15)', text: 'var(--status-danger, #ef4444)', border: 'rgba(239, 68, 68, 0.3)' },
    medium: { bg: 'rgba(245, 158, 11, 0.15)', text: 'var(--status-warning, #f59e0b)', border: 'rgba(245, 158, 11, 0.3)' },
    low:    { bg: 'rgba(34, 197, 94, 0.15)',  text: 'var(--status-success, #22c55e)', border: 'rgba(34, 197, 94, 0.3)' },
  };
  const c = colors[level] || colors.low;
  return <span style={{ ...badgeBase, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>{level}</span>;
}

function SeverityBadge({ severity }) {
  const colors = { critical: { bg: '#fef2f2', text: '#991b1b' }, warning: { bg: '#fffbeb', text: '#92400e' }, info: { bg: '#eff6ff', text: '#1e40af' } };
  const c = colors[severity] || colors.info;
  return <span style={{ ...badgeBase, background: c.bg, color: c.text }}>{severity}</span>;
}

// ── Shared Styles ─────────────────────────────────────────────

const cardStyle = { background: 'var(--card-bg, #fff)', borderRadius: 8, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid var(--border-light, #e5e7eb)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--border-light, #e5e7eb)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 12px', borderBottom: '1px solid var(--border-light, #f3f4f6)' };
const badgeBase = { padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500, textTransform: 'capitalize' };
const selectStyle = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-light, #d1d5db)', fontSize: 13, background: 'var(--card-bg, #fff)', color: 'var(--text-primary, #111)', cursor: 'pointer' };
const btnPrimary = { padding: '8px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { padding: '6px 16px', borderRadius: 6, border: '1px solid var(--border-light, #d1d5db)', background: 'var(--card-bg, #fff)', color: 'var(--text-primary, #374151)', fontSize: 13, cursor: 'pointer' };
const anomalyRow = { padding: '10px 12px', background: 'var(--bg-secondary, #fafafa)', borderRadius: 6 };
const projLabel = { margin: '0 0 4px', fontWeight: 600, color: '#374151' };
const projValue = { margin: '2px 0', color: '#6b7280' };
